/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useMemo, memo, useCallback } from 'react';
import PropTypes from 'prop-types';
// @ts-expect-error no typings
import RecordPropType from 'conversations-prop-types/prop-types/RecordPropType';
import openCallingWidget from 'calling-global-api/methods/openCallingWidget';
import { isUninitialized, isLoading, getData } from 'calling-lifecycle-internal/async-flux-utils/operators/getApiProperties';
import RegisterPhoneNumberPrompt from 'calling-lifecycle-internal/onboarding/components/RegisterPhoneNumberPrompt';
import { getPersistedCallProvider } from 'calling-lifecycle-internal/utils/getLocalCallSettings';
import { getIsProviderHubSpot } from 'calling-lifecycle-internal/call-provider/operators/getIsTwilioBasedCallProvider';
import getLocalDevelopmentWidgetInfo from 'calling-integration-sdk-support/external-provider/operators/getLocalDevelopmentWidgetInfo';
import { getPhoneNumbers, getConnectAccountNumbers, getHubspotProvidedNumbers, getHubspotSubAccountNumbers, getInstalledCallingApplications, getHubSpotCallingEnabled } from '../../callee-omnibus/operators/settingsOmnibusResultGetters';
import GlobalCallingDisabled from './GlobalCallingDisabled';
import UnlockCallingItem from 'calling-orchestration-shared-library/unlock-calling/components/UnlockCallingItem';
import { CommunicatorLogger } from 'calling-lifecycle-usage-tracker/callingTracker';
import CallNumberSkeletons from './CallNumberSkeletons';
import { hasPaidSeatAccess } from 'calling-orchestration-shared-library/hub-info/utils/hasCallingFeature';
import { hasCallingMinutesPerPortal } from 'calling-orchestration-shared-library/hub-info/utils/getCallingLimits';
import { logCallingError } from 'calling-error-reporting/report/error';
import { OPEN_PROVIDER_MENU } from 'calling-integration-cross-tab/types/BroadcastEvents';
import { createBroadcastMessage } from 'calling-integration-cross-tab/messages/createBroadcastMessages';
import { sendCrossTabMessage } from 'calling-cross-tab-library/external/sendCrossTabMessage';
import { CALLING_EXTENSIONS_CLICK_TO_CALL_MIGRATION } from 'calling-internal-common/constants/CallingGates';
import { jsx as _jsx } from "react/jsx-runtime";
function CallingOnboarding({
  asyncSettingsOmnibus,
  children,
  appIdentifier,
  gates
}) {
  const isOmnibusDataUninitialized = isUninitialized(asyncSettingsOmnibus);
  const isOmnibusDataLoading = isLoading(asyncSettingsOmnibus);
  const omnibusData = getData(asyncSettingsOmnibus);
  const provider = getPersistedCallProvider();
  const isHubSpotProvider = getIsProviderHubSpot(provider);
  const phoneNumbers = getPhoneNumbers(omnibusData);
  const hubspotSubAccountNumbers = getHubspotSubAccountNumbers(phoneNumbers);
  const hubspotProvidedNumbers = getHubspotProvidedNumbers(phoneNumbers);
  const twilioAccountNumbers = getConnectAccountNumbers(phoneNumbers);
  const hasHubspotSubAccountOrProvidedNumbers = hubspotSubAccountNumbers && hubspotSubAccountNumbers.size > 0 || hubspotProvidedNumbers && hubspotProvidedNumbers.size > 0;
  const installedCallingApplications = getInstalledCallingApplications(omnibusData);
  const connectedAppsWithLocalStorageOverrides = getLocalDevelopmentWidgetInfo(installedCallingApplications);
  const hasThirdPartyCalling = connectedAppsWithLocalStorageOverrides && connectedAppsWithLocalStorageOverrides.size > 0;

  // show when hubspot provider, globally disabled and no other third party/connect options
  const shouldShowCallingDisabled = useMemo(() => !getHubSpotCallingEnabled(omnibusData) && isHubSpotProvider && !hasThirdPartyCalling && twilioAccountNumbers.size === 0, [hasThirdPartyCalling, isHubSpotProvider, omnibusData, twilioAccountNumbers.size]);

  // show when hubspot provider, no calling paid seat and no other third party options
  const shouldShowFreeCalling = useMemo(() => !hasPaidSeatAccess() && isHubSpotProvider && !hasThirdPartyCalling, [hasThirdPartyCalling, isHubSpotProvider]);
  const handleTracking = useCallback(() => {
    CommunicatorLogger.log('calling_callUpgradeClicked', {
      source: appIdentifier
    });
  }, [appIdentifier]);
  const isUngatedForThirdPartyClickToCallMigration = useMemo(() => {
    return gates.includes(CALLING_EXTENSIONS_CLICK_TO_CALL_MIGRATION);
  }, [gates]);
  const handleChangeProvider = () => {
    const payload = {
      showProviderMenu: true
    };
    if (isUngatedForThirdPartyClickToCallMigration) {
      sendCrossTabMessage(createBroadcastMessage({
        type: OPEN_PROVIDER_MENU,
        payload
      })).catch(error => {
        logCallingError({
          error,
          tags: {
            requestName: 'sendCrossTabMessage - open provider menu'
          }
        });
      });
    } else {
      openCallingWidget(payload);
    }
  };

  // show loading state
  if (isOmnibusDataUninitialized || isOmnibusDataLoading) return /*#__PURE__*/_jsx(CallNumberSkeletons, {});
  if (shouldShowCallingDisabled) return /*#__PURE__*/_jsx(GlobalCallingDisabled, {});
  if (shouldShowFreeCalling) {
    if (hasCallingMinutesPerPortal()) {
      return /*#__PURE__*/_jsx(UnlockCallingItem, {
        blockingType: "paid-seat-blocking",
        use: "popover",
        "data-test-id": "paid-seat-blocking-popover"
      });
    }
    return /*#__PURE__*/_jsx(UnlockCallingItem, {
      blockingType: "free-account-blocking",
      use: "popover",
      onTrack: handleTracking,
      "data-test-id": "free-account-blocking-popover"
    });
  }

  // Show this when HS provider selected but no HS phone numbers
  // NOTE: you can show this regardless of existence of 3rd party calling since there is a provider switch link
  if (isHubSpotProvider && !hasHubspotSubAccountOrProvidedNumbers) return /*#__PURE__*/_jsx(RegisterPhoneNumberPrompt, {
    shouldShowProviderSelectionLink: true,
    handleProviderSwitch: handleChangeProvider
  });
  return children;
}
CallingOnboarding.propTypes = {
  asyncSettingsOmnibus: RecordPropType('AsyncSettingsOmnibusResult').isRequired,
  children: PropTypes.node.isRequired,
  onRegisterFromPhoneNumber: PropTypes.func.isRequired,
  onShowOnboardingIntro: PropTypes.func.isRequired,
  onClosePopover: PropTypes.func.isRequired,
  appIdentifier: PropTypes.string.isRequired
};
export default /*#__PURE__*/memo(CallingOnboarding);